/**
 * Tournament browse/discovery service (ADR-031 P4).
 */

import * as arenaRepository from "../repositories/arenaRepository";
import * as tournamentEventRepository from "../repositories/tournamentEventRepository";
import * as tournamentEntryRepository from "../repositories/tournamentEntryRepository";
import {
  filterBrowseCompetitions,
  projectBrowseCompetitionCard,
  isCompetitionBrowseable,
  canSelfRegisterEntry,
  isSelfRegistrationOffered,
  getRegistrationSettings,
  isRegistrationWindowOpen,
  entryOwnedByPlayer,
  playerRegistrationState,
} from "../domain/competition/selfRegistration";
import { isCompetitionPubliclyViewable } from "./publicTournamentService";
import { ENTRY_STATUSES } from "../domain/competition/entryManagementTypes";
import { resolveEventDisplayName } from "../domain/event/eventCompetition";

/**
 * @param {{ query?: string, status?: string, state?: string, club?: string, category?: string, sportId?: string, max?: number }} [filters]
 */
export async function listBrowseCompetitions(filters = {}) {
  const raw = await arenaRepository.listRecentCompetitions({
    max: filters.max || 60,
    fromServer: filters.fromServer !== false,
  });
  const filtered = filterBrowseCompetitions(raw, filters);
  return filtered.map(projectBrowseCompetitionCard);
}

function projectPublicEvent(event) {
  return {
    eventId: event.eventId,
    name: resolveEventDisplayName(event),
    display: event.display || { name: resolveEventDisplayName(event) },
    competition: event.competition || null,
    status: event.status || "draft",
    createdAtMs: event.createdAtMs || null,
  };
}

/**
 * Detail view for browse / register.
 * @param {string} competitionId
 * @param {{ uid?: string | null, playerId?: string | null }} [viewer]
 */
export async function getBrowseCompetitionDetail(competitionId, viewer = {}, opts = {}) {
  const fromServer = opts.fromServer !== false;
  const competition = await arenaRepository.fetchArena(competitionId, {
    fromServer,
    skipHydrate: true,
  });
  if (!competition || !isCompetitionPubliclyViewable(competition) || !isCompetitionBrowseable(competition)) {
    return null;
  }

  let eventDocs = [];
  try {
    eventDocs = await tournamentEventRepository.listEvents(competitionId, { fromServer });
  } catch {
    eventDocs = [];
  }
  const events = (eventDocs.length ? eventDocs : competition.managedEvents || []).map(projectPublicEvent);

  let myEntries = [];
  if (viewer.uid || viewer.playerId) {
    try {
      const rows = await tournamentEntryRepository.listEntriesForPlayer(
        competitionId,
        events.map((event) => event.eventId).filter(Boolean),
        { uid: viewer.uid || null, playerId: viewer.playerId || null, fromServer }
      );
      myEntries = rows.filter((entry) =>
        entryOwnedByPlayer(entry, viewer.uid, viewer.playerId || null)
      );
    } catch {
      myEntries = [];
    }
  }

  const card = projectBrowseCompetitionCard({ ...competition, managedEvents: events });
  const entries = myEntries.filter(
    (e) => e.status === ENTRY_STATUSES.PENDING || e.status === ENTRY_STATUSES.CONFIRMED
  );

  // CTA visibility for guests + signed-in players (auth enforced on click / create).
  const registerable = isSelfRegistrationOffered(competition);

  return {
    competition,
    card,
    events,
    venue: competition.venue || null,
    dates: competition.dates || null,
    description: competition.description || "",
    rules:
      competition.settings?.rulesText ||
      competition.settings?.defaultMatchFormat ||
      null,
    registeredPlayers: entries.map((e) => ({
      entryId: e.entryId,
      label: e.label,
      eventId: e.eventId,
      category: e.category,
      status: e.status,
    })),
    myEntries: myEntries.map((e) => ({
      ...e,
      registrationState: playerRegistrationState(e),
    })),
    registration: {
      ...getRegistrationSettings(competition),
      windowOpen: isRegistrationWindowOpen(competition),
      canSelfRegister: registerable,
      closeMs: competition.dates?.registrationCloseMs ?? null,
      openMs: competition.dates?.registrationOpenMs ?? null,
    },
    tournamentStatus: competition.status,
  };
}

export {
  canSelfRegisterEntry,
  isSelfRegistrationOffered,
  isRegistrationWindowOpen,
  getRegistrationSettings,
};
